"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import type {
  HotelResultsV2AmenityItem,
  HotelResultsV2Item,
} from "../adapters/hotelResultsV2.model";
import type { HotelResultsV2ViewMode } from "../types";
import { toHotelResultsV2FaNumber } from "../utils";

function StarRow({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, index) => (
        <PhosphorIcon
          key={index}
          name="Star"
          size={12}
          weight="fill"
          className={
            index < count
              ? "fill-amber-400 text-amber-400"
              : "fill-gray-200 text-gray-200"
          }
        />
      ))}
    </div>
  );
}

function ScoreBadge({
  score,
  reviewCount,
}: {
  score?: number;
  reviewCount?: number;
}) {
  if (typeof score !== "number" || typeof reviewCount !== "number") {
    return null;
  }

  const label = score >= 9 ? "عالی" : score >= 8 ? "خیلی خوب" : "خوب";
  const shortCount =
    reviewCount >= 1000 ? `${(reviewCount / 1000).toFixed(1)}k` : reviewCount;

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <span className="text-sm" style={{ color: "#6b7280" }}>
        {label} · {shortCount} نظر
      </span>
      <span
        className="rounded-md px-2 py-0.5 text-base"
        style={{
          backgroundColor: "#f0fdf4",
          color: "#166534",
          fontWeight: 700,
        }}
      >
        {score.toFixed(1)}
      </span>
    </div>
  );
}

function OfferChips({
  offers,
  limit,
  compact,
}: {
  offers: HotelResultsV2AmenityItem[];
  limit: number;
  compact?: boolean;
}) {
  if (!offers.length) return null;

  const visibleItems = offers.slice(0, limit);
  const hiddenCount = offers.length - visibleItems.length;

  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? "mt-2" : "mt-4"}`}>
      {visibleItems.map((offer) => (
        <span
          key={offer.key}
          className={`inline-flex items-center gap-1 rounded-full border border-primary/15 bg-primary/5 font-semibold text-primary ${
            compact ? "px-2 py-1 text-xs" : "px-2.5 py-1 text-sm"
          }`}
          title={offer.label}
        >
          <PhosphorIcon name={offer.icon} size={compact ? 12 : 14} />
          <span className="max-w-[150px] truncate">{offer.label}</span>
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          className={`inline-flex items-center rounded-full border border-slate-200 bg-slate-50 font-semibold text-slate-500 ${
            compact ? "px-2 py-1 text-xs" : "px-2.5 py-1 text-sm"
          }`}
        >
          +{toHotelResultsV2FaNumber(hiddenCount)}
        </span>
      )}
    </div>
  );
}

interface HotelCardProps {
  hotel: HotelResultsV2Item;
  viewMode: HotelResultsV2ViewMode;
  index: number;
  staySummaryLabel: string;
  mobileCard?: boolean;
}

export default function HotelCard({
  hotel,
  viewMode,
  index,
  staySummaryLabel,
  mobileCard,
}: HotelCardProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const hasSpecialOffer = Boolean(hotel.isFeatured);
  const isUnavailable = hotel.isAvailable === false;
  const hotelHref = hotel.href ?? "#";
  const primaryImage = hotel.images[0];
  const offerItems = hotel.offerItems ?? [];
  const supplierTags = hotel.tags ?? [];

  const handleHotelNavigation = (event: React.MouseEvent) => {
    if (!hotel.href || isNavigating) {
      event.preventDefault();
      return;
    }

    setIsNavigating(true);
  };

  const discountPercent =
    hotel.originalPrice && hotel.pricePerNight
      ? Math.round(
          ((hotel.originalPrice - hotel.pricePerNight) / hotel.originalPrice) *
            100,
        )
      : 0;

  if (viewMode === "list" && !mobileCard) {
    return (
      <article
        className="overflow-hidden rounded-lg border border-border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:border-primary/15 hover:shadow-[0_8px_18px_rgba(15,23,42,0.06)]"
        dir="rtl"
      >
        <div className="flex cursor-pointer flex-col md:min-h-[238px] md:flex-row">
          <ImageArea
            hotel={hotel}
            image={primaryImage}
            index={index}
            discountPercent={discountPercent}
            hasSpecialOffer={hasSpecialOffer}
            href={hotelHref}
            onNavigate={handleHotelNavigation}
            mode="list"
          />

          <div className="flex min-w-0 flex-1 flex-col justify-between p-5">
            <div>
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link
                    href={hotelHref}
                    onClick={handleHotelNavigation}
                    className="block text-lg font-bold leading-7 text-foreground transition hover:text-primary"
                  >
                    {hotel.name}
                  </Link>
                  <StarRow count={hotel.stars} />
                  <div className="mt-1 flex items-center gap-1">
                    <PhosphorIcon
                      name="MapPin"
                      size={14}
                      className="shrink-0 text-muted-foreground"
                    />
                    <p className="truncate text-sm text-muted-foreground">
                      {hotel.location}
                    </p>
                  </div>
                </div>
                <ScoreBadge
                  score={hotel.score}
                  reviewCount={hotel.reviewCount}
                />
              </div>

              <SupplierTags tags={supplierTags} />
              <OfferChips offers={offerItems} limit={4} />
            </div>

            <div className="mt-2 border-t border-border pt-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  {hotel.originalPrice && (
                    <p className="text-sm text-muted-foreground line-through">
                      {hotel.originalPrice.toLocaleString("fa-IR")}
                    </p>
                  )}
                  <HotelPriceLine
                    pricePerNight={hotel.pricePerNight}
                    discountPercent={discountPercent}
                    size="lg"
                    staySummaryLabel={staySummaryLabel}
                    isUnavailable={isUnavailable}
                  />
                </div>
                <div
                  className="flex shrink-0 gap-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Link
                    href={hotelHref}
                    onClick={handleHotelNavigation}
                    aria-disabled={isNavigating}
                    className="flex min-w-[142px] items-center justify-center gap-1 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 aria-disabled:pointer-events-none aria-disabled:opacity-75"
                    data-analytics-event="hotel_result_clicked"
                    data-analytics-id={`results-v2-hotel-${hotel.id}`}
                    data-analytics-name={hotel.name}
                    data-analytics-type="link"
                    data-analytics-funnel="hotel_booking"
                    data-analytics-step="results_hotel_card_cta"
                    data-analytics-property-hotel-id={hotel.id}
                    data-analytics-property-hotel-name={hotel.name}
                    data-analytics-property-hotel-href={hotelHref}
                    data-analytics-property-price={String(
                      hotel.pricePerNight ?? "",
                    )}
                    data-analytics-property-available={String(!isUnavailable)}
                  >
                    {isUnavailable ? (
                      <>
                        مشاهده هتل
                        <PhosphorIcon
                          name={isNavigating ? "Spinner" : "CaretLeft"}
                          size={14}
                          className={isNavigating ? "animate-spin" : undefined}
                        />
                      </>
                    ) : (
                      <>
                        مشاهده و انتخاب اتاق
                        <PhosphorIcon
                          name={isNavigating ? "Spinner" : "CaretLeft"}
                          size={14}
                          className={isNavigating ? "animate-spin" : undefined}
                        />
                      </>
                    )}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className="relative flex cursor-pointer flex-col rounded-lg border border-border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:border-primary/15 hover:shadow-[0_8px_18px_rgba(15,23,42,0.06)]"
      dir="rtl"
    >
      <ImageArea
        hotel={hotel}
        image={primaryImage}
        index={index}
        discountPercent={discountPercent}
        hasSpecialOffer={hasSpecialOffer}
        href={hotelHref}
        onNavigate={handleHotelNavigation}
        mode="grid"
        mobileCard={mobileCard}
      />

      <div className="flex flex-1 flex-col p-4">
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Link
              href={hotelHref}
              onClick={handleHotelNavigation}
              className="block text-lg font-bold leading-7 text-foreground transition hover:text-primary"
            >
              {hotel.name}
            </Link>
            <StarRow count={hotel.stars} />
          </div>
          <ScoreBadge score={hotel.score} reviewCount={hotel.reviewCount} />
        </div>

        <div className="mb-1 mt-1 flex items-center gap-1">
          <PhosphorIcon
            name="MapPin"
            size={14}
            className="shrink-0 text-muted-foreground"
          />
          <p className="truncate text-base text-muted-foreground">
            {hotel.location}
          </p>
        </div>
        <SupplierTags tags={supplierTags} compact />
        <OfferChips offers={offerItems} limit={mobileCard ? 3 : 2} compact />

        <div className="flex-1" />

        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-2 min-w-0">
            {hotel.originalPrice && (
              <p className="text-sm leading-none text-muted-foreground line-through">
                {hotel.originalPrice.toLocaleString("fa-IR")} تومان
              </p>
            )}
            <HotelPriceLine
              pricePerNight={hotel.pricePerNight}
              discountPercent={discountPercent}
              size="md"
              staySummaryLabel={staySummaryLabel}
              isUnavailable={isUnavailable}
            />
          </div>

          <div
            className="flex flex-col gap-2 sm:flex-row"
            onClick={(event) => event.stopPropagation()}
          >
            <Link
              href={hotelHref}
              onClick={handleHotelNavigation}
              aria-disabled={isNavigating}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 aria-disabled:pointer-events-none aria-disabled:opacity-75"
              data-analytics-event="hotel_result_clicked"
              data-analytics-id={`results-v2-hotel-${hotel.id}`}
              data-analytics-name={hotel.name}
              data-analytics-type="link"
              data-analytics-funnel="hotel_booking"
              data-analytics-step="results_hotel_card_cta"
              data-analytics-property-hotel-id={hotel.id}
              data-analytics-property-hotel-name={hotel.name}
              data-analytics-property-hotel-href={hotelHref}
              data-analytics-property-price={String(hotel.pricePerNight ?? "")}
              data-analytics-property-available={String(!isUnavailable)}
            >
              {isUnavailable ? (
                <>
                  مشاهده هتل
                  <PhosphorIcon
                    name={isNavigating ? "Spinner" : "CaretLeft"}
                    size={14}
                    className={isNavigating ? "animate-spin" : undefined}
                  />
                </>
              ) : (
                <>
                  مشاهده و انتخاب اتاق
                  <PhosphorIcon
                    name={isNavigating ? "Spinner" : "CaretLeft"}
                    size={14}
                    className={isNavigating ? "animate-spin" : undefined}
                  />
                </>
              )}
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function ImageArea({
  hotel,
  image,
  index,
  discountPercent,
  hasSpecialOffer,
  mode,
  href,
  onNavigate,
  mobileCard,
}: {
  hotel: HotelResultsV2Item;
  image?: string;
  index: number;
  discountPercent: number;
  hasSpecialOffer: boolean;
  mode: HotelResultsV2ViewMode;
  href: string;
  onNavigate: (event: React.MouseEvent) => void;
  mobileCard?: boolean;
}) {
  const imageElement = (
    <img
      src={image}
      alt={hotel.name}
      width={mode === "list" ? 680 : 420}
      height={mode === "list" ? 476 : 224}
      className="block h-full max-h-full min-h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
      loading={index < (mode === "list" ? 2 : 3) ? "eager" : "lazy"}
      fetchPriority={index < (mode === "list" ? 2 : 3) ? "high" : "auto"}
    />
  );

  return (
    <div
      className={
        mode === "list"
          ? "relative h-48 w-full shrink-0 overflow-hidden bg-muted md:h-[238px] md:w-[340px]"
          : "relative h-56 max-h-56 min-h-56 shrink-0 overflow-hidden rounded-t-lg bg-muted"
      }
    >
      {image ? (
        <Link
          href={href}
          onClick={onNavigate}
          className="block h-full w-full"
          aria-label={`مشاهده ${hotel.name}`}
        >
          {imageElement}
        </Link>
      ) : (
        <div className="grid h-full w-full place-items-center bg-slate-100 text-xs font-semibold text-slate-400">
          تصویر هتل
        </div>
      )}

      <div className="absolute right-2 top-2 flex flex-col gap-1">
        {hotel.isAvailable === false && (
          <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white/95 px-2.5 py-1 text-xs font-bold text-red-600 shadow-[0_8px_18px_rgba(220,38,38,0.16)] backdrop-blur">
            <PhosphorIcon name="Prohibit" size={12} weight="bold" />
            ظرفیت تکمیل
          </span>
        )}
        {hasSpecialOffer && <SpecialOfferCountdownBadge />}
      </div>
    </div>
  );
}

function SpecialOfferCountdownBadge() {
  const [timeLeft, setTimeLeft] = useState(() => getOfferCountdownParts());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTimeLeft(getOfferCountdownParts());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-white shadow-[0_8px_18px_rgba(232,64,64,0.22)]"
      style={{ backgroundColor: "#e84040", fontWeight: 700 }}
    >
      <PhosphorIcon name="Lightning" size={12} weight="fill" />
      پیشنهاد ویژه
      <span className="font-black tabular-nums" dir="ltr">
        {timeLeft.join(":")}
      </span>
    </span>
  );
}

function getOfferCountdownParts() {
  const offerCycleMs = 8 * 60 * 60 * 1000;
  const remainingMs = offerCycleMs - (Date.now() % offerCycleMs);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((value) =>
    value.toLocaleString("fa-IR", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    }),
  );
}

function SupplierTags({
  tags,
  compact,
}: {
  tags: string[];
  compact?: boolean;
}) {
  if (!tags.length) return null;

  const visibleTags = tags.slice(0, 3);
  const hiddenCount = tags.length - visibleTags.length;

  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? "mt-2" : "mt-3"}`}>
      {visibleTags.map((tag) => (
        <span
          key={tag}
          className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 font-semibold text-slate-600 ${
            compact ? "px-2 py-1 text-xs" : "px-2.5 py-1 text-sm"
          }`}
        >
          <PhosphorIcon name="Tag" size={compact ? 12 : 14} />
          <span className="max-w-[150px] truncate">{tag}</span>
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          className={`inline-flex items-center rounded-full border border-slate-200 bg-slate-50 font-semibold text-slate-500 ${
            compact ? "px-2 py-1 text-xs" : "px-2.5 py-1 text-sm"
          }`}
        >
          +{toHotelResultsV2FaNumber(hiddenCount)}
        </span>
      )}
    </div>
  );
}

function HotelPriceLine({
  discountPercent,
  isUnavailable,
  pricePerNight,
  size,
  staySummaryLabel,
}: {
  discountPercent: number;
  isUnavailable: boolean;
  pricePerNight?: number;
  size: "lg" | "md";
  staySummaryLabel: string;
}) {
  if (isUnavailable) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1 font-bold text-red-600 ${
          size === "lg" ? "text-base" : "text-sm"
        }`}
      >
        <PhosphorIcon name="Prohibit" size={size === "lg" ? 15 : 13} weight="bold" />
        تکمیل ظرفیت
      </span>
    );
  }

  if (typeof pricePerNight !== "number" || pricePerNight <= 0) {
    return (
      <p
        className={`whitespace-nowrap font-bold text-slate-500 ${
          size === "lg" ? "text-lg" : "text-base"
        }`}
      >
        قیمت در حال بررسی
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <p
        className={`whitespace-nowrap font-extrabold text-foreground ${
          size === "lg" ? "text-xl" : "mt-0.5 text-lg"
        }`}
      >
        از {pricePerNight.toLocaleString("fa-IR")}
        <span className="mr-1 text-sm font-medium">تومان</span>
      </p>
      <span className="text-sm font-semibold text-slate-300">/</span>
      <span
        className={`inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground ${
          size === "lg" ? "text-sm" : "text-xs"
        }`}
      >
        <PhosphorIcon name="Users" size={size === "lg" ? 14 : 12} />
        {staySummaryLabel}
      </span>
      {discountPercent > 0 && (
        <DiscountBadge
          discountPercent={discountPercent}
          alignEnd={size === "md"}
        />
      )}
    </div>
  );
}

function DiscountBadge({
  discountPercent,
  alignEnd,
}: {
  discountPercent: number;
  alignEnd?: boolean;
}) {
  return (
    <span
      className={`inline-flex align-middle rounded-full bg-emerald-50 px-2.5 py-1 text-sm font-extrabold text-emerald-700 ${
        alignEnd ? "mr-auto" : "mr-2"
      }`}
    >
      %{toHotelResultsV2FaNumber(discountPercent)} تخفیف
    </span>
  );
}
